import React, { useState, useEffect, useCallback } from "react";
/**
 * Props:
 * - snoozeDays: number of days to stay hidden after dismissal (default 7)
 * - showAfterMs: delay before the popup appears on a fresh visit (default 2000)
 * - storageKey: localStorage key used to remember dismissal (default "newsletter_popup_dismissed_until")
 * - signupUrl: the signup page the button links to
 * - title / description: copy shown in the popup
 * - buttonLabel: text on the CTA button
 */
export default function NewsletterPopup({
  snoozeDays = 7,
  showAfterMs = 2000,
  storageKey = "newsletter_popup_dismissed_until",
  signupUrl = "https://tnyurl.ca/MCNewsSignup",
  title = "Stay in the loop",
  description = "Get the latest product updates, helpful MCC EAM/CMMS tips, and industry insights delivered straight to your inbox.",
  buttonLabel = "Subscribe",
}) {
  const [visible, setVisible] = useState(false);

  // Decide whether we're allowed to show the popup right now
  useEffect(() => {
    let dismissedUntil = null;
    try {
      dismissedUntil = window.localStorage.getItem(storageKey);
    } catch (e) {
      // localStorage unavailable (privacy mode, SSR, etc.) — fail open, just don't persist
    }

    const now = Date.now();
    const isSnoozed = dismissedUntil && Number(dismissedUntil) > now;
    if (isSnoozed) return;

    const timer = setTimeout(() => setVisible(true), showAfterMs);
    return () => clearTimeout(timer);
  }, [showAfterMs, storageKey]);

  const snooze = useCallback(() => {
    try {
      const until = Date.now() + snoozeDays * 24 * 60 * 60 * 1000;
      window.localStorage.setItem(storageKey, String(until));
    } catch (e) {
      // ignore storage failures — worst case the popup reappears next visit
    }
  }, [snoozeDays, storageKey]);

  const handleClose = () => {
    snooze();
    setVisible(false);
  };

  const handleSignupClick = () => {
    snooze();
    setVisible(false);
  };

  // Close on Escape
  useEffect(() => {
    if (!visible) return;
    const onKey = (e) => e.key === "Escape" && handleClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  if (!visible) return null;

  return (
    <div className="newsletter-overlay" onClick={handleClose}>
      <div
        className="newsletter-popup"
        role="dialog"
        aria-modal="true"
        aria-labelledby="newsletter-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="newsletter-close"
          onClick={handleClose}
          aria-label="Close newsletter signup"
        >
          &times;
        </button>

        {title && (
          <h2 id="newsletter-title" className="newsletter-title">{title}</h2>
        )}
        {description && (
          <p className="newsletter-description">{description}</p>
        )}
        <a
          href={signupUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="newsletter-submit"
          onClick={handleSignupClick}
        >
          {buttonLabel}
        </a>
      </div>
    </div>
  );
}